import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { CircularProgress } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import HistoryIcon from '@mui/icons-material/History'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ReactMarkdown from 'react-markdown'
import { useSelector } from 'react-redux'
import { triggerBpAudit, listBpAudits, getBpAudit, exportBpAuditPdf } from 'services/reportingService'
import { toast } from 'react-toastify'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { getCurrentUser } from 'features/auth/ducks/selectors'

const AUDIT_PHASES = [
  'Collecting documents and metadata…',
  'Checking compliance and renewals…',
  'Checking duplicates and document chains…',
  'Composing your information risk assessment…',
]

const SEVERITY_OVERVIEW = {
  low:      { label: 'Low risk',    color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  medium:   { label: 'Medium risk', color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  high:     { label: 'High risk',   color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  critical: { label: 'Critical',    color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
}

function parseNarrative(raw) {
  if (!raw || typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed.startsWith('{')) return null
  try {
    const parsed = JSON.parse(trimmed)
    if (parsed && typeof parsed === 'object') return parsed
  } catch (_) {}
  return null
}

const BpAuditTab = ({ bpId, bpName }) => {
  const bp = useSelector(getSelectedBp)
  const currentUser = useSelector(getCurrentUser)
  // Owner = creator OR explicit owner-pivot user. Matches backend isOwner().
  const isOwner = !!currentUser && !!bp && (
    bp.creatorId === currentUser.id ||
    (bp.owners || []).some(o => o.id === currentUser.id)
  )
  const [audits, setAudits] = useState([])
  const [selectedAudit, setSelectedAudit] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [phaseIndex, setPhaseIndex] = useState(0)
  const [insightFile, setInsightFile] = useState(null)
  const hasAutoTriggered = useRef(false)

  const handleFileClick = (file) => {
    if (!file?.fileId) return
    setInsightFile({
      id: file.fileId,
      displayName: file.name || file.fileName,
      buildingPassportId: bpId,
      buildingPassportName: bpName,
    })
  }

  const fetchAudits = useCallback(async () => {
    try {
      const res = await listBpAudits(bpId)
      const list = res.data.data || []
      setAudits(list)
      const pending = list.find(a => a.status === 'pending' || a.status === 'processing')
      const latest = list.find(a => a.status === 'completed')
      // Flip running BEFORE loading clears so there's no empty frame while we
      // wait for the auto-trigger effect to fire. Only auto-trigger for the
      // owner — viewers with BP access can read history but not create audits.
      if (pending || (list.length === 0 && isOwner && !hasAutoTriggered.current)) {
        setRunning(true)
      }
      if (latest) {
        try {
          const r = await getBpAudit(bpId, latest.id)
          setSelectedAudit(r.data.data)
        } catch (_) {}
      }
      if (list.length === 0 && isOwner && !hasAutoTriggered.current) {
        hasAutoTriggered.current = true
        triggerBpAudit(bpId)
          .then(() => toast.success('Audit started'))
          .catch(err => {
            setRunning(false)
            toast.error(err.response?.data?.error || 'Failed to start audit')
          })
      }
    } catch (_) {
    } finally {
      setLoading(false)
    }
  }, [bpId])

  useEffect(() => { fetchAudits() }, [fetchAudits])

  // Poll while an audit is in flight
  useEffect(() => {
    if (!running) return
    const id = setInterval(async () => {
      try {
        const res = await listBpAudits(bpId)
        const list = res.data.data || []
        setAudits(list)
        const pending = list.find(a => a.status === 'pending' || a.status === 'processing')
        if (!pending) {
          setRunning(false)
          const completed = list.find(a => a.status === 'completed')
          if (completed) {
            const r = await getBpAudit(bpId, completed.id)
            setSelectedAudit(r.data.data)
          }
        }
      } catch (_) {}
    }, 5000)
    return () => clearInterval(id)
  }, [running, bpId])

  // Rotate phase label while running
  useEffect(() => {
    if (!running) return
    const id = setInterval(() => setPhaseIndex(i => (i + 1) % AUDIT_PHASES.length), 12000)
    return () => clearInterval(id)
  }, [running])

  const handleTrigger = () => {
    setRunning(true)
    triggerBpAudit(bpId)
      .then(() => toast.success('Audit started'))
      .catch(err => {
        setRunning(false)
        toast.error(err.response?.data?.error || 'Failed to start audit')
      })
  }

  const handleSelectAudit = (auditId) => {
    getBpAudit(bpId, auditId)
      .then(r => setSelectedAudit(r.data.data))
      .catch(() => toast.error('Failed to load audit'))
  }

  const [exportingPdf, setExportingPdf] = useState(false)
  const handleExportPdf = async () => {
    if (!selectedAudit || selectedAudit.status !== 'completed' || exportingPdf) return
    setExportingPdf(true)
    try {
      const result = await exportBpAuditPdf(bpId, selectedAudit.id)
      const blob = new Blob([result.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const disposition = result.headers?.['content-disposition'] || ''
      const match = disposition.match(/filename="?([^"]+)"?/i)
      link.download = match ? match[1] : `information-risk-assessment-${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      toast.error('Failed to export PDF')
    } finally {
      setExportingPdf(false)
    }
  }

  if (loading) return <LoadingState><CircularProgress size={28} sx={{ color: '#D24724' }} /></LoadingState>

  const report = selectedAudit?.reportData
  const narrative = parseNarrative(selectedAudit?.aiNarrative)
  const building = report?.buildings?.[0]

  return (
    <Card>
      <TopBar>
        <TopBarLeft>
          {selectedAudit && !running && isOwner && (
            <RunAgainLink onClick={handleTrigger} title="Run a fresh audit">
              <RefreshIcon style={{ fontSize: 14 }} /> Run new audit
            </RunAgainLink>
          )}
          {audits.length > 0 && (
            <AuditHistory>
              <HistoryIcon style={{ fontSize: 14, color: '#999' }} />
              {audits.slice(0, 5).map(a => (
                <HistoryChip key={a.id} $active={selectedAudit?.id === a.id} $status={a.status}
                  onClick={() => a.status === 'completed' && handleSelectAudit(a.id)}>
                  {a.createdAt?.split(' ')[0]}{a.status === 'processing' && ' …'}
                </HistoryChip>
              ))}
            </AuditHistory>
          )}
        </TopBarLeft>
        {selectedAudit?.status === 'completed' && (
          <ExportPdfButton onClick={handleExportPdf} disabled={exportingPdf} title="Download Information Risk Assessment as PDF">
            {exportingPdf
              ? <CircularProgress size={14} sx={{ color: '#D24724' }} />
              : <PictureAsPdfIcon style={{ fontSize: 16 }} />}
            <span>{exportingPdf ? 'Exporting…' : 'Export PDF'}</span>
          </ExportPdfButton>
        )}
      </TopBar>

      {running && (
        <AuditSkeleton>
          <SkPhaseRow>
            <CircularProgress size={18} sx={{ color: '#D24724' }} />
            <SkPhaseText>{AUDIT_PHASES[phaseIndex]}</SkPhaseText>
            <SkPhaseHint>Typically takes 45–90 seconds</SkPhaseHint>
          </SkPhaseRow>
          <SkSummaryBar>
            <SkChip /><SkChip /><SkChip /><SkChip />
          </SkSummaryBar>
          <SkCard>
            <SkTitle />
            <SkLine $w="100%" />
            <SkLine $w="92%" />
            <SkLine $w="80%" />
            <SkLine $w="95%" />
            <SkLine $w="60%" />
          </SkCard>
        </AuditSkeleton>
      )}

      {!running && !selectedAudit && (
        <EmptyAudit>
          <EmptyAuditTitle>No audit yet</EmptyAuditTitle>
          <EmptyAuditText>
            {isOwner
              ? 'Run an audit to get an AI-powered information risk assessment for this Building Passport.'
              : 'No audits have been run for this Building Passport yet. Only the Building Passport owner can run a new audit.'}
          </EmptyAuditText>
          {isOwner && (
            <RunAuditBtn onClick={handleTrigger}>
              <PlayArrowIcon style={{ fontSize: 18 }} /> Run Audit
            </RunAuditBtn>
          )}
        </EmptyAudit>
      )}

      {!running && selectedAudit && report && (
        <Report>
          <ReportMeta>Audit completed {selectedAudit.completedAt} — {selectedAudit.fileCount} documents</ReportMeta>

          {report.totals && (
            <SummaryStrip>
              <Chip>{report.totals.files?.toLocaleString()} documents</Chip>
              {report.totals.expired > 0 && <Chip $warn>{report.totals.expired} overdue</Chip>}
              {report.totals.expiringSoon > 0 && <Chip $caution>{report.totals.expiringSoon} expiring 90d</Chip>}
              {report.totals.renewed > 0 && <Chip $ok>{report.totals.renewed} renewed</Chip>}
            </SummaryStrip>
          )}

          {narrative?.overview && (
            <Section>
              <SectionHead>Information Risk Assessment</SectionHead>
              <NarrativeCard $sev={SEVERITY_OVERVIEW[narrative.overviewSeverity || 'medium']}>
                {narrative.overviewSeverity && (
                  <SevBadge $sev={SEVERITY_OVERVIEW[narrative.overviewSeverity]}>{SEVERITY_OVERVIEW[narrative.overviewSeverity]?.label}</SevBadge>
                )}
                <NarrativeText>{narrative.overview}</NarrativeText>
              </NarrativeCard>
            </Section>
          )}

          {narrative?.buildings?.[0]?.items?.length > 0 && (
            <Section>
              <SectionHead>Risk Items</SectionHead>
              <RiskList>
                {narrative.buildings[0].items.map((item, i) => (
                  <RiskRow key={i}>
                    <RiskReq>{item.requirement}</RiskReq>
                    <SevBadge $sev={SEVERITY_OVERVIEW[item.severity] || SEVERITY_OVERVIEW.medium}>
                      {SEVERITY_OVERVIEW[item.severity]?.label || item.severity}
                    </SevBadge>
                    <RiskBody>
                      <div>{item.risk}</div>
                      {item.regulationRef && item.regulationRef !== 'N/A' && (
                        <RiskRef>{item.regulationRef}</RiskRef>
                      )}
                    </RiskBody>
                  </RiskRow>
                ))}
              </RiskList>
            </Section>
          )}

          {narrative?.recommendations?.length > 0 && (
            <Section>
              <SectionHead>Recommended Actions</SectionHead>
              <Recs>
                {narrative.recommendations.map((rec, i) => (
                  <Rec key={i}>
                    <RecTitle>{rec.action}</RecTitle>
                    {rec.reason && <RecReason>{rec.reason}</RecReason>}
                  </Rec>
                ))}
              </Recs>
            </Section>
          )}

          {selectedAudit.aiNarrative && !narrative && (
            <Section>
              <SectionHead>Summary</SectionHead>
              <NarrativeCard $sev={SEVERITY_OVERVIEW.medium}>
                <NarrativeMarkdown><ReactMarkdown>{selectedAudit.aiNarrative}</ReactMarkdown></NarrativeMarkdown>
              </NarrativeCard>
            </Section>
          )}

          {report.complianceGaps?.[0]?.missing?.length > 0 && (
            <Section>
              <SectionHead><ErrorOutlineIcon style={{ fontSize: 16, color: '#dc2626' }} /> Compliance Gaps</SectionHead>
              <GapDescription>Key UK compliance document types missing for this building.</GapDescription>
              <GapChips>
                {report.complianceGaps[0].missing.map((m, i) => (
                  <GapChip key={i}>{m}</GapChip>
                ))}
              </GapChips>
            </Section>
          )}

          {report.expired?.length > 0 && (
            <Section>
              <SectionHead><WarningAmberIcon style={{ fontSize: 16, color: '#dc2626' }} /> Overdue for Renewal ({report.expired.length})</SectionHead>
              <FileList>
                {report.expired.map((f, i) => (
                  <FileRow key={i} $clickable={!!f.fileId} onClick={() => handleFileClick(f)}>
                    <FileName>{f.name}</FileName>
                    <FileMeta>{f.type || '—'} — expired {f.date} <Muted>({f.source})</Muted></FileMeta>
                  </FileRow>
                ))}
              </FileList>
            </Section>
          )}

          {report.expiringSoon?.length > 0 && (
            <Section>
              <SectionHead>Expiring Within 90 Days ({report.expiringSoon.length})</SectionHead>
              <FileList>
                {report.expiringSoon.map((f, i) => (
                  <FileRow key={i} $clickable={!!f.fileId} onClick={() => handleFileClick(f)}>
                    <FileName>{f.name}</FileName>
                    <FileMeta>{f.type || '—'} — expires {f.date}</FileMeta>
                  </FileRow>
                ))}
              </FileList>
            </Section>
          )}

          {report.renewed?.length > 0 && (
            <Section>
              <SectionHead><CheckCircleIcon style={{ fontSize: 16, color: '#059669' }} /> Successfully Renewed ({report.renewed.length})</SectionHead>
              <FileList>
                {report.renewed.map((f, i) => (
                  <FileRow key={i} $clickable={!!f.fileId} onClick={() => handleFileClick(f)}>
                    <FileName>{f.name}</FileName>
                    <FileMeta>{f.type || '—'}</FileMeta>
                  </FileRow>
                ))}
              </FileList>
            </Section>
          )}
        </Report>
      )}

      {insightFile && (
        <GuruInsightModal
          file={insightFile}
          onClose={() => setInsightFile(null)}
        />
      )}
    </Card>
  )
}

export default BpAuditTab

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 8px;
  padding: 20px 22px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  @media (max-width: 640px) {
    padding: 16px;
  }
`
const LoadingState = styled.div` display: flex; justify-content: center; padding: 48px; `
const TopBar = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; gap: 10px; flex-wrap: wrap;
`
const TopBarLeft = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex: 1;
`
const ExportPdfButton = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 6px;
  font-size: 12px; font-weight: 500; cursor: pointer;
  flex-shrink: 0;
  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.colors.primary}; color: ${({ theme }) => theme.colors.primary}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`
const RunAgainLink = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  background: none; border: none; cursor: pointer;
  font-size: 12px; color: ${({ theme }) => theme.colors.primary};
  padding: 4px 8px; border-radius: 4px;
  &:hover { background: #fbede9; }
`
const AuditHistory = styled.div` display: flex; align-items: center; gap: 6px; flex-wrap: wrap; `
const HistoryChip = styled.button`
  padding: 4px 10px; font-size: 11px; border-radius: 4px;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : (theme.colors.grey?.[500] || '#e6e6e6')};
  background: ${({ $active }) => $active ? '#fbede9' : 'white'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.haiti};
  cursor: ${({ $status }) => $status === 'completed' ? 'pointer' : 'default'};
  font-weight: ${({ $active }) => $active ? 600 : 400};
`

const AuditSkeleton = styled.div` display: flex; flex-direction: column; gap: 16px; `
const SkPhaseRow = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 16px 18px;
  background: #fff7f2; border: 1px solid #fbcbbd; border-radius: 8px;
`
const SkPhaseText = styled.div` font-size: 14px; font-weight: 500; color: #78350f; `
const SkPhaseHint = styled.div` font-size: 12px; color: #9a8b84; margin-left: auto; `
const SkSummaryBar = styled.div` display: flex; gap: 8px; `
const SkChip = styled.div`
  height: 26px; width: 110px; border-radius: 13px;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite;
`
const SkCard = styled.div`
  padding: 18px; background: #fafafa; border: 1px solid #e5e7eb; border-radius: 8px;
  display: flex; flex-direction: column; gap: 10px;
`
const SkTitle = styled.div`
  height: 18px; width: 180px; border-radius: 4px;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite;
`
const SkLine = styled.div`
  height: 12px; width: ${({ $w }) => $w || '100%'}; border-radius: 4px;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite;
`

const Report = styled.div` display: flex; flex-direction: column; gap: 20px; `
const ReportMeta = styled.div` font-size: 12px; color: #6b7280; `
const SummaryStrip = styled.div` display: flex; flex-wrap: wrap; gap: 8px; `
const Chip = styled.span`
  padding: 5px 12px; border-radius: 14px; font-size: 12px; font-weight: 500;
  background: ${({ $warn, $caution, $ok }) => $warn ? '#fef2f2' : $caution ? '#fffbeb' : $ok ? '#ecfdf5' : '#f4f4f5'};
  color: ${({ $warn, $caution, $ok }) => $warn ? '#991b1b' : $caution ? '#92400e' : $ok ? '#065f46' : '#374151'};
  border: 1px solid ${({ $warn, $caution, $ok }) => $warn ? '#fecaca' : $caution ? '#fde68a' : $ok ? '#a7f3d0' : '#e5e7eb'};
`

const Section = styled.section` display: flex; flex-direction: column; gap: 10px; `
const SectionHead = styled.h3`
  margin: 0; font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.colors.haiti};
  text-transform: uppercase; letter-spacing: 0.5px;
  display: flex; align-items: center; gap: 6px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary}; padding-left: 8px;
`

const NarrativeCard = styled.div`
  padding: 14px 16px; border-radius: 6px;
  background: ${({ $sev }) => $sev?.bg || '#fffbeb'};
  border: 1px solid ${({ $sev }) => $sev?.border || '#fde68a'};
`
const SevBadge = styled.span`
  display: inline-block; padding: 3px 9px; border-radius: 3px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase;
  background: ${({ $sev }) => $sev?.bg || '#fffbeb'};
  border: 1px solid ${({ $sev }) => $sev?.border || '#fde68a'};
  color: ${({ $sev }) => $sev?.color || '#92400e'};
  margin-bottom: 6px;
`
const NarrativeText = styled.div` font-size: 14px; line-height: 1.55; color: #1f2937; `
const NarrativeMarkdown = styled.div`
  font-size: 13px; line-height: 1.55; color: #1f2937;
  p { margin: 0 0 8px; } p:last-child { margin-bottom: 0; }
`

const RiskList = styled.div` display: flex; flex-direction: column; gap: 12px; `
const RiskRow = styled.div`
  display: grid; grid-template-columns: 200px 100px 1fr; gap: 10px; align-items: start;
  padding: 12px; border: 1px solid #e5e7eb; border-radius: 6px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`
const RiskReq = styled.div` font-size: 13px; font-weight: 600; color: #111827; `
const RiskBody = styled.div` font-size: 13px; color: #374151; line-height: 1.5; `
const RiskRef = styled.div` font-size: 11px; color: #6b7280; font-style: italic; margin-top: 4px; `

const Recs = styled.ol` margin: 0 0 0 18px; padding: 0; display: flex; flex-direction: column; gap: 8px; `
const Rec = styled.li` font-size: 13px; color: #1f2937; `
const RecTitle = styled.strong` font-weight: 600; color: #111827; `
const RecReason = styled.div` font-size: 12px; color: #4b5563; margin-top: 2px; `

const GapDescription = styled.p` font-size: 12px; color: #6b7280; margin: 0; `
const GapChips = styled.div` display: flex; flex-wrap: wrap; gap: 6px; `
const GapChip = styled.span`
  padding: 3px 9px; border-radius: 10px; font-size: 11px;
  background: #fef2f2; color: #991b1b; border: 1px solid #fecaca;
`

const FileList = styled.div` display: flex; flex-direction: column; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; `
const FileRow = styled.div`
  padding: 10px 12px; border-bottom: 1px solid #f3f4f6;
  &:last-child { border-bottom: none; }
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
    &:hover { background: #fafafa; }
  `}
`
const FileName = styled.div` font-size: 13px; font-weight: 500; color: #111827; `
const FileMeta = styled.div` font-size: 11px; color: #6b7280; margin-top: 2px; `
const Muted = styled.span` color: #9ca3af; `

const EmptyAudit = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 48px 24px; text-align: center;
`
const EmptyAuditTitle = styled.h3` font-size: 16px; font-weight: 600; color: ${({ theme }) => theme.colors.haiti}; margin: 0; `
const EmptyAuditText = styled.p` font-size: 13px; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; margin: 0 0 8px; max-width: 420px; line-height: 1.5; `
const RunAuditBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px;
  background: ${({ theme }) => theme.colors.primary}; color: white;
  border: none; border-radius: 6px;
  font-size: 13px; font-weight: 500; cursor: pointer;
  &:hover { opacity: 0.9; }
`
